import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Table,
  Input,
  Select,
  Button,
  Card,
  DatePicker,
  Space,
  Tooltip,
  message,
  Dropdown,
  Tag,
} from 'antd';
import {
  SearchOutlined,
  CheckCircleOutlined,
  SendOutlined,
  PercentageOutlined,
  FilterOutlined,
  InfoCircleOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import useDebounce from '../../../../../../hooks/useDebounce';
import { flowsGet, flowsInsights, flowsInsightSources } from '../../../../api/Api';
import WhatsAppFlowPreview from '../preview/WhatsAppFlowPreview';
import { Modal } from 'antd';
import Chevron from '../../../../../../assets/customSVG/ChevronCrm';
import Options from '../../../../../../assets/customSVG/Options';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const { RangePicker } = DatePicker;

const QUESTION_COMPONENT_TYPES = new Set([
  'TextInput',
  'TextArea',
  'DatePicker',
  'RadioButtonsGroup',
  'CheckboxGroup',
  'Dropdown',
  'OptIn',
]);

// Frontend-only, collapsed status set
const UNIFIED_STATUS = {
  FAILED: 'failed',
  PENDING: 'pending',
  COMPLETED: 'completed',
};

const STATUS_STYLES = {
  [UNIFIED_STATUS.COMPLETED]: {
    className: 'bg-green-100 text-green-700',
    label: 'Completed',
  },
  [UNIFIED_STATUS.PENDING]: {
    className: 'bg-yellow-100 text-yellow-700',
    label: 'Pending',
  },
  [UNIFIED_STATUS.FAILED]: {
    className: 'bg-red-100 text-red-700',
    label: 'Failed',
  },
};

const STATUS_OPTIONS = [
  { label: 'All Status', value: 'all' },
  { label: 'Pending', value: UNIFIED_STATUS.PENDING },
  { label: 'Completed', value: UNIFIED_STATUS.COMPLETED },
  { label: 'Failed', value: UNIFIED_STATUS.FAILED },
];

// Backend enum reference:
// failed:-2, bounced:-1, draft:0, sending:1, sent:2, delivered:3, completed:10
const BACKEND_NUM_TO_STR = {
  [-2]: 'failed',
  [-1]: 'bounced',
  [0]: 'draft',
  [1]: 'sending',
  [2]: 'sent',
  [3]: 'delivered',
  [10]: 'completed',
};

// Frontend -> Backend list mapping (for API filter)
const BACKEND_STATUS_MAP = {
  [UNIFIED_STATUS.FAILED]: ['failed', 'bounced'],
  [UNIFIED_STATUS.PENDING]: ['draft', 'sending', 'sent', 'delivered'],
  [UNIFIED_STATUS.COMPLETED]: ['completed'],
};

const normalizeBackendStatus = (raw) => {
  if (raw === null || raw === undefined) return '';
  if (typeof raw === 'number') return BACKEND_NUM_TO_STR[raw] || '';
  if (typeof raw === 'string') return raw.toLowerCase();
  return String(raw).toLowerCase();
};

const mapBackendToUnified = (backendStatus) => {
  const s = normalizeBackendStatus(backendStatus);
  if (!s) return UNIFIED_STATUS.PENDING; // default fallback
  if (['failed', 'bounced'].includes(s)) return UNIFIED_STATUS.FAILED;
  if (['draft', 'sending', 'sent', 'delivered'].includes(s)) return UNIFIED_STATUS.PENDING;
  if (s === 'completed') return UNIFIED_STATUS.COMPLETED;
  return UNIFIED_STATUS.PENDING;
};

const uniqueArray = (values = []) =>
  Array.from(new Set(values.filter(Boolean)));

const sanitizeSource = (value) => {
  if (!value || typeof value !== 'string') return '';
  return value
    .replace(/\${.*?}/g, '')
    .replace(/[_/]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const toAnswerKeyFragment = (value) => {
  const sanitized = sanitizeSource(value);
  if (!sanitized) return '';
  return sanitized
    .split(' ')
    .filter(Boolean)
    .map((word, index) => {
      const lower = word.toLowerCase();
      if (index === 0) {
        return lower.charAt(0).toUpperCase() + lower.slice(1);
      }
      return lower;
    })
    .join('_');
};

// Normalizes API answer keys ("0_Select 1763...", etc.) so they can be matched
// against label fragments regardless of spacing, case, or timestamps.
const normalizeAnswerKey = (value) => {
  if (!value) return '';
  return String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
};

const toSnakeLower = (value) => {
  const sanitized = sanitizeSource(value);
  if (!sanitized) return '';
  return sanitized
    .split(' ')
    .filter(Boolean)
    .map((word) => word.toLowerCase())
    .join('_');
};

const parseFlowJsonSafe = (raw) => {
  if (!raw) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch (error) {
      console.error('Unable to parse flow_json string', error);
      return null;
    }
  }
  if (typeof raw === 'object') {
    try {
      return JSON.parse(JSON.stringify(raw));
    } catch (error) {
      console.error('Unable to clone flow_json object', error);
      return raw;
    }
  }
  return null;
};

const extractFlowQuestions = (flowJson) => {
  if (!flowJson || !Array.isArray(flowJson.screens)) return [];

  const questions = [];

  flowJson.screens.forEach((screen, screenIndex) => {
    const counters = new Map();

    const traverse = (node) => {
      if (!node) return;
      if (Array.isArray(node)) {
        node.forEach((child) => traverse(child));
        return;
      }
      if (typeof node !== 'object') return;

      if (node.layout) traverse(node.layout);
      if (Array.isArray(node.children)) traverse(node.children);

      if (!QUESTION_COMPONENT_TYPES.has(node.type)) return;

      const labelCandidate = node.label || node.title || node.text || node.name;
      if (!labelCandidate) return;

      const primaryFragment = toAnswerKeyFragment(labelCandidate);
      const nameFragment = toAnswerKeyFragment(node.name);
      const fallbackFragment = toSnakeLower(labelCandidate);
      const nameFallbackFragment = toSnakeLower(node.name);
      const matchKey =
        primaryFragment || nameFragment || fallbackFragment || nameFallbackFragment;

      if (!matchKey) return;

      const counterKey = `${screenIndex}:${matchKey}`;
      const occurrence = counters.get(counterKey) || 0;
      counters.set(counterKey, occurrence + 1);

      const fragments = uniqueArray([
        primaryFragment,
        nameFragment,
        fallbackFragment,
        nameFallbackFragment,
      ]);

      const candidates = uniqueArray(
        fragments.flatMap((fragment) => [
          `screen_${screenIndex}_${fragment}_${occurrence}`,
          `screen_${screenIndex}_${fragment}`,
        ]),
      );

      questions.push({
        id: `${screenIndex}-${matchKey}-${occurrence}`,
        label: labelCandidate,
        screenIndex,
        occurrence,
        candidates,
        fragments,
      });
    };

    traverse(screen?.layout);
  });

  return questions;
};

const resolveAnswerValue = (answers, question) => {
  if (!answers || !question) return undefined;

  for (const key of question.candidates) {
    if (Object.prototype.hasOwnProperty.call(answers, key)) {
      return answers[key];
    }
  }

  const entries = Object.entries(answers).map(([key, value]) => ({
    key,
    value,
    normalizedKey: normalizeAnswerKey(key),
  }));

  for (const fragment of question.fragments) {
    if (!fragment) continue;
    const normalizedFragment = normalizeAnswerKey(fragment);
    const match = entries.find((entry) =>
      entry.normalizedKey.includes(normalizedFragment),
    );
    if (match) {
      return match.value;
    }
  }

  return undefined;
};

const formatAnswerValue = (value) => {
  if (value === null || value === undefined) return '-';

  const cleanValue = (val) => {
    if (typeof val !== 'string') return val;

    // Remove any auto-generated numeric prefixes (e.g., "0_select_one_option")
    const withoutPrefix = val.replace(/^\d+_/, '');

    // Drop trailing timestamps that follow the new "Label 1234567890123" format
    const withoutTimestamp = withoutPrefix.replace(/\s+\d{10,}$/, '');

    // Replace underscores with spaces for readability
    const withSpaces = withoutTimestamp.replace(/_/g, ' ');

    return withSpaces.trim();
  };

  if (Array.isArray(value)) {
    if (value.length === 0) return '-';
    return value.map(cleanValue).join(', ');
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (error) {
      return String(value);
    }
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  const cleanedValue = cleanValue(String(value)).trim();
  return cleanedValue || '-';
};

const formatPhoneNumber = (countryCode, phoneNumber) => {
  const normalizedCode = countryCode
    ? `+${String(countryCode).replace(/^\+/, '')}`
    : '';
  const normalizedNumber = phoneNumber ? String(phoneNumber) : '';
  if (normalizedCode && normalizedNumber) {
    return `${normalizedCode} ${normalizedNumber}`;
  }
  return normalizedCode || normalizedNumber || '-';
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const parsed = dayjs(value);
  if (!parsed.isValid()) return '-';
  return parsed.format('YYYY-MM-DD HH:mm');
};

const makeUniqueColumnNames = (columns) => {
  const counts = {};
  return columns.map((col) => {
    const title = col.title;
    if (counts[title] !== undefined) {
      counts[title] += 1;
      return {
        ...col,
        title: `${title} (${counts[title]})`,
        uniqueTitle: `${title}_${counts[title]}`,
      };
    } else {
      counts[title] = 0;
      return {
        ...col,
        uniqueTitle: title,
      };
    }
  });
};

const DATE_FORMAT = 'YYYY-MM-DD';
const SOURCE_TYPE_META = {
  broadcast: { label: 'Broadcast', tagColor: 'blue' },
  sequence: { label: 'Sequence', tagColor: 'purple' },
};

const FlowInsights = () => {
  const { flowId } = useParams();
  const { currentBrand } = useSelector((state) => state.app);

  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebounce(searchText, 400);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState(() => [
    dayjs().subtract(6, 'day'),
    dayjs(),
  ]);
  const [hideDuplicates, setHideDuplicates] = useState(false);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [isSourcesLoading, setIsSourcesLoading] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isFlowLoading, setIsFlowLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [rawMetrics, setRawMetrics] = useState([]);
  const [summary, setSummary] = useState(null);
  const [flowQuestions, setFlowQuestions] = useState([]);
  const [flowMeta, setFlowMeta] = useState({ name: '' });
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [parsedFlowJson, setParsedFlowJson] = useState(null);

  const brandId = currentBrand?.brand_id;

  const resetToFirstPage = useCallback(() => {
    setPagination((prev) => {
      if (prev.current === 1) return prev;
      return { ...prev, current: 1 };
    });
  }, []);

  useEffect(() => {
    resetToFirstPage();
  }, [
    debouncedSearch,
    statusFilter,
    dateRange,
    flowId,
    brandId,
    hideDuplicates,
    selectedSource,
    resetToFirstPage,
  ]);

  const queryParams = useMemo(() => {
    const params = {
      page: pagination.current,
      limit: pagination.pageSize,
    };

    if (debouncedSearch) params.search = debouncedSearch;
    if (statusFilter && statusFilter !== 'all') {
      const backendStatuses = BACKEND_STATUS_MAP[statusFilter];
      if (backendStatuses?.length) {
        params.status = backendStatuses.join(','); // e.g., "draft,sending,sent,delivered"
      }
    }

    if (dateRange && dateRange[0] && dateRange[1]) {
      params.start_date = dateRange[0].format(DATE_FORMAT);
      params.end_date = dateRange[1].format(DATE_FORMAT);
    }

    if (selectedSource?.source_type === 'broadcast') {
      params.broadcast_id = selectedSource.source_id;
    } else if (selectedSource?.source_type === 'sequence') {
      params.campaign_sequence_id = selectedSource.source_id;
    }

    if (hideDuplicates) {
      // backend expects string 'true'
      params.hide_duplicates = 'true';
    }

    return params;
  }, [
    pagination.current,
    pagination.pageSize,
    debouncedSearch,
    statusFilter,
    dateRange,
    selectedSource,
    hideDuplicates,
  ]);

  const fetchInsightSources = useCallback(async () => {
    if (!brandId || !flowId) {
      setSourceOptions([]);
      return;
    }

    setIsSourcesLoading(true);
    try {
      const response = await flowsInsightSources(flowId, brandId);
      const sources =
        response?.data?.sources || response?.sources || response?.data || [];
      setSourceOptions(Array.isArray(sources) ? sources : []);
    } catch (error) {
      console.error('Failed to fetch flow insight sources:', error);
      message.error('Unable to load broadcast/sequence filters.');
      setSourceOptions([]);
    } finally {
      setIsSourcesLoading(false);
    }
  }, [brandId, flowId]);

  const fetchFlowDefinition = useCallback(async () => {
    if (!brandId || !flowId) return;

    setIsFlowLoading(true);
    try {
      const response = await flowsGet(flowId, brandId, true);
      const flowPayload =
        response?.data?.flow ||
        response?.data?.data ||
        response?.data ||
        response;

      const parsedJson = parseFlowJsonSafe(
        flowPayload?.flow_json ?? flowPayload?.flowJson,
      );

      setParsedFlowJson(parsedJson);

      const questions = extractFlowQuestions(parsedJson);
      setFlowQuestions(questions);
      setFlowMeta({
        name: flowPayload?.name || flowPayload?.flow_name || '',
      });
    } catch (error) {
      console.error('Failed to fetch flow definition:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to load flow definition.';
      message.error(errorMessage);
      setFlowQuestions([]);
    } finally {
      setIsFlowLoading(false);
    }
  }, [brandId, flowId]);

  const fetchInsights = useCallback(async () => {
    if (!brandId || !flowId) return;

    setIsLoading(true);
    try {
      const response = await flowsInsights(flowId, brandId, queryParams);

      const metrics =
        response?.data?.metrics || response?.metrics || response?.data || [];
      const summaryPayload =
        response?.data?.summary || response?.summary || null;
      const paginationPayload =
        response?.data?.pagination || response?.pagination || {};

      const totalFromResponse =
        paginationPayload?.total ??
        paginationPayload?.count ??
        response?.data?.total ??
        metrics.length;

      setRawMetrics(Array.isArray(metrics) ? metrics : []);
      setSummary(summaryPayload);
      setPagination((prev) => ({
        ...prev,
        total:
          typeof totalFromResponse === 'number'
            ? totalFromResponse
            : prev.total,
        pageSize: queryParams.limit || prev.pageSize,
        current: queryParams.page || prev.current,
      }));
    } catch (error) {
      console.error('Failed to fetch flow insights:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to load flow insights.';
      message.error(errorMessage);
      setRawMetrics([]);
    } finally {
      setIsLoading(false);
    }
  }, [brandId, flowId, queryParams]);

  useEffect(() => {
    fetchInsightSources();
  }, [fetchInsightSources]);

  useEffect(() => {
    if (!selectedSource) return;
    const stillExists = sourceOptions.some(
      (source) =>
        source.source_type === selectedSource.source_type &&
        String(source.source_id) === String(selectedSource.source_id),
    );
    if (!stillExists) {
      setSelectedSource(null);
    }
  }, [sourceOptions, selectedSource]);

  useEffect(() => {
    fetchFlowDefinition();
  }, [fetchFlowDefinition]);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  const statsData = useMemo(() => {
    const delivered = summary?.flow_delivered_count ?? 0;
    const completed = summary?.flow_completed_count ?? 0;
    const rawRatio = summary?.flow_completion_ratio;
    let ratio = 0;
    if (typeof rawRatio === 'number') {
      ratio = rawRatio <= 1 ? rawRatio * 100 : rawRatio;
    }
    return {
      delivered,
      completed,
      completionRatio: Number.isFinite(ratio) ? Number(ratio.toFixed(1)) : 0,
    };
  }, [summary]);

  const sourceSelectOptions = useMemo(() => {
    if (!Array.isArray(sourceOptions) || sourceOptions.length === 0) return [];

    const grouped = sourceOptions.reduce((acc, source) => {
      const typeKey = source.source_type || 'other';
      const meta =
        SOURCE_TYPE_META[typeKey] || {
          label: typeKey ? typeKey.replace(/_/g, ' ') : 'Other',
          tagColor: 'default',
        };

      if (!acc[typeKey]) {
        acc[typeKey] = {
          label: `${meta.label}${meta.label.endsWith('s') ? '' : 's'}`,
          options: [],
        };
      }

      acc[typeKey].options.push({
        label: (
          <div className="flex items-center justify-between gap-3">
            <span className="truncate">{source.source_name}</span>
            <Tag color={meta.tagColor} className="m-0">
              {meta.label}
            </Tag>
          </div>
        ),
        value: `${typeKey}:${source.source_id}`,
        display: source.source_name,
        raw: source,
      });

      return acc;
    }, {});

    return Object.values(grouped).map((group) => ({
      ...group,
      options: group.options.sort((a, b) =>
        a.display.localeCompare(b.display),
      ),
    }));
  }, [sourceOptions]);

  const tableData = useMemo(() => {
    if (!Array.isArray(rawMetrics)) return [];

    return rawMetrics.map((item, index) => {
      const answers = {};
      flowQuestions.forEach((question) => {
        const answerValue = resolveAnswerValue(item?.answers || {}, question);
        answers[question.id] = formatAnswerValue(answerValue);
      });

      const fullName = `${item?.contact_first_name || ''} ${item?.contact_last_name || ''}`
        .trim()
        .replace(/\s+/g, ' ');

      return {
        key:
          item?.id ??
          item?.contact_id ??
          `${item?.contact_phone_number ?? 'contact'}-${index}`,
        name: fullName || item?.contact_name || '-',
        phoneNumber: formatPhoneNumber(
          item?.contact_phone_country_code,
          item?.contact_phone_number,
        ),
        broadcastName: item?.broadcast_name || '-',
        sequenceName: item?.campaign_sequence_name || '-',
        status: mapBackendToUnified(item?.status),
        deliveredAt: formatDateTime(item?.delivered_at),
        completedAt: formatDateTime(item?.completed_at),
        answers,
      };
    });
  }, [rawMetrics, flowQuestions]);

  const baseColumns = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        width: 200,
        fixed: 'left',
        render: (text) => (
          <span className="font-medium text-gray-900">{text || '-'}</span>
        ),
      },
      {
        title: 'Phone Number',
        dataIndex: 'phoneNumber',
        key: 'phoneNumber',
        width: 180,
      },
      {
        title: 'Broadcast Name',
        dataIndex: 'broadcastName',
        key: 'broadcastName',
        width: 200,
        render: (value) => value || '-',
      },
      {
        title: 'Sequence Name',
        dataIndex: 'sequenceName',
        key: 'sequenceName',
        width: 200,
        render: (value) => value || '-',
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 140,
        render: (status) => {
          if (!status || status === '-') {
            return (
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                Unknown
              </span>
            );
          }
          const normalized = String(status).toLowerCase();
          const meta = STATUS_STYLES[normalized] || {
            className: 'bg-gray-100 text-gray-700',
            label: status,
          };
          const label =
            meta.label ||
            normalized.charAt(0).toUpperCase() + normalized.slice(1);

          return (
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium ${meta.className}`}
            >
              {label}
            </span>
          );
        },
      },
      {
        title: 'Delivered At',
        dataIndex: 'deliveredAt',
        key: 'deliveredAt',
        width: 180,
      },
      {
        title: 'Completed At',
        dataIndex: 'completedAt',
        key: 'completedAt',
        width: 180,
      },
    ],
    [],
  );

  const questionColumns = useMemo(
    () =>
      flowQuestions.map((question) => ({
        title: question.label,
        dataIndex: ['answers', question.id],
        key: `question-${question.id}`,
        width: 220,
        render: (value) => value || '-',
      })),
    [flowQuestions],
  );

  const allColumns = useMemo(
    () => [...baseColumns, ...questionColumns],
    [baseColumns, questionColumns],
  );

  const columns = useMemo(
    () => makeUniqueColumnNames(allColumns),
    [allColumns],
  );

  // ---------- EXPORT HELPERS ----------
  const buildDownloadParams = (totalCountOverride) => {
    const p = { ...queryParams };
    p.page = 1;
    if (typeof totalCountOverride === 'number' && totalCountOverride > 0) {
      p.limit = totalCountOverride;
    } else {
      // keep current limit; backend may cap it
      p.limit = queryParams.limit || 1000;
    }
    // backend requires string 'true'
    if (hideDuplicates) p.hide_duplicates = 'true';
    return p;
  };

  const prepareExportRows = (records) => {
    return records.map((item) => {
      const row = {};
      columns.forEach((col) => {
        const uniqueTitle = col.uniqueTitle || col.title;

        if (col.dataIndex === 'name') {
          row[uniqueTitle] = `${item?.contact_first_name || ''} ${item?.contact_last_name || ''}`
            .trim()
            .replace(/\s+/g, ' ') || item?.contact_name || '-';
        } else if (col.dataIndex === 'phoneNumber') {
          row[uniqueTitle] = formatPhoneNumber(
            item?.contact_phone_country_code,
            item?.contact_phone_number,
          );
        } else if (col.dataIndex === 'broadcastName') {
          row[uniqueTitle] = item?.broadcast_name || '-';
        } else if (col.dataIndex === 'sequenceName') {
          row[uniqueTitle] = item?.campaign_sequence_name || '-';
        } else if (col.dataIndex === 'status') {
          const unified = mapBackendToUnified(item?.status);
          row[uniqueTitle] = unified || '-';
        } else if (col.dataIndex === 'deliveredAt') {
          row[uniqueTitle] = formatDateTime(item?.delivered_at);
        } else if (col.dataIndex === 'completedAt') {
          row[uniqueTitle] = formatDateTime(item?.completed_at);
        } else if (Array.isArray(col.dataIndex) && col.dataIndex[0] === 'answers') {
          const questionId = col.dataIndex[1];
          const question = flowQuestions.find((q) => q.id === questionId);
          if (question) {
            const answerValue = resolveAnswerValue(item?.answers || {}, question);
            row[uniqueTitle] = formatAnswerValue(answerValue);
          }
        }
      });
      return row;
    });
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleDownloadXlsx = async () => {
    if (!brandId || !flowId) {
      message.error('Missing required information to download data.');
      return;
    }

    const totalCount = summary?.total_count || pagination.total || 0;
    if (totalCount === 0) {
      message.warning('No data available to download.');
      return;
    }

    setIsDownloading(true);
    message.loading({ content: 'Preparing XLSX…', key: 'download', duration: 0 });

    try {
      const downloadParams = buildDownloadParams(totalCount);
      const response = await flowsInsights(flowId, brandId, downloadParams);
      const allMetrics =
        response?.data?.metrics || response?.metrics || response?.data || [];

      if (!Array.isArray(allMetrics) || allMetrics.length === 0) {
        message.warning('No data found to export.');
        setIsDownloading(false);
        return;
      }

      const excelData = prepareExportRows(allMetrics);

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Flow Insights');

      const maxWidth = 50;
      const colWidths = Object.keys(excelData[0] || {}).map((key) => {
        const maxLength = Math.max(
          key.length,
          ...excelData.map((row) => String(row[key] || '').length),
        );
        return { wch: Math.min(maxLength + 2, maxWidth) };
      });
      worksheet['!cols'] = colWidths;

      const fileName = `Flow_Insights_${flowMeta.name || flowId}_${dayjs().format(
        'YYYY-MM-DD_HHmmss',
      )}.xlsx`;

      XLSX.writeFile(workbook, fileName);

      message.success({
        content: `Downloaded ${allMetrics.length} records as XLSX.`,
        key: 'download',
        duration: 3,
      });
    } catch (error) {
      console.error('Failed to download XLSX:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to download XLSX.';
      message.error({ content: errorMessage, key: 'download', duration: 3 });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadCsv = async () => {
    if (!brandId || !flowId) {
      message.error('Missing required information to download data.');
      return;
    }

    const totalCount = summary?.total_count || pagination.total || 0;
    if (totalCount === 0) {
      message.warning('No data available to download.');
      return;
    }

    setIsDownloading(true);
    message.loading({ content: 'Preparing CSV…', key: 'download', duration: 0 });

    try {
      const downloadParams = buildDownloadParams(totalCount);
      const response = await flowsInsights(flowId, brandId, downloadParams);
      const allMetrics =
        response?.data?.metrics || response?.metrics || response?.data || [];

      if (!Array.isArray(allMetrics) || allMetrics.length === 0) {
        message.warning('No data found to export.');
        setIsDownloading(false);
        return;
      }

      const rows = prepareExportRows(allMetrics);
      const ws = XLSX.utils.json_to_sheet(rows);
      const csv = XLSX.utils.sheet_to_csv(ws);

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const fileName = `Flow_Insights_${flowMeta.name || flowId}_${dayjs().format(
        'YYYY-MM-DD_HHmmss',
      )}.csv`;
      downloadBlob(blob, fileName);

      message.success({
        content: `Downloaded ${allMetrics.length} records as CSV.`,
        key: 'download',
        duration: 3,
      });
    } catch (error) {
      console.error('Failed to download CSV:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to download CSV.';
      message.error({ content: errorMessage, key: 'download', duration: 3 });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSourceFilterChange = (value, option) => {
    if (!value) {
      setSelectedSource(null);
      return;
    }
    if (option?.raw) {
      setSelectedSource(option.raw);
      return;
    }
    const [type, id] = String(value).split(':');
    if (!type || !id) {
      setSelectedSource(null);
      return;
    }
    setSelectedSource({
      source_type: type,
      source_id: Number(id) || id,
      source_name: option?.display || '',
    });
  };

  const filterSourceOption = (input, option) => {
    if (!option?.display) return false;
    return option.display.toLowerCase().includes(input.trim().toLowerCase());
  };

  const handleDateChange = (dates) => {
    if (!dates || !dates[0] || !dates[1]) {
      setDateRange(null);
      return;
    }
    setDateRange(dates);
  };

  const handleTableChange = (nextPagination) => {
    setPagination((prev) => ({
      ...prev,
      current: nextPagination.current,
      pageSize: nextPagination.pageSize,
    }));
  };

  const isEmpty = tableData.length === 0;
  const displayedTotal = pagination.total || summary?.total_count || 0;

  // ---------- ACTIONS DROPDOWN ----------
  const actionItems = [
    {
      key: 'xlsx',
      label: 'Download as XLSX',
      disabled: isEmpty || isLoading || isDownloading,
      onClick: handleDownloadXlsx,
    },
    {
      key: 'csv',
      label: 'Download as CSV',
      disabled: isEmpty || isLoading || isDownloading,
      onClick: handleDownloadCsv,
    },
    {
      type: 'divider',
    },
    {
      key: 'hideDuplicates',
      // show a checkmark when active
      label: (
        <div className="flex items-center gap-2">
          <span>{hideDuplicates ? '✓' : ''}</span>
          <span>Hide duplicates</span>
        </div>
      ),
      onClick: () => {
        setHideDuplicates((prev) => !prev);
        // fetch will auto-run via effect because hideDuplicates in deps
      },
    },
  ];

  const menu = {
    items: actionItems.filter(Boolean).map((item) => {
      if (item.type === 'divider') return { type: 'divider' };
      return {
        key: item.key,
        label: item.label,
        disabled: item.disabled,
        onClick: item.onClick,
      };
    }),
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">

        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Flow Insights</h1>
            <p className="text-gray-600 mt-1">Track and analyze flow completion metrics</p>
            {flowMeta?.name && (
              <p className="text-md text-gray-500 mt-2">
                Flow Name: <span className="font-medium text-gray-800">{flowMeta.name}</span>
              </p>
            )}
          </div>

          <Button
            type="primary"
            onClick={() => setIsPreviewVisible(true)}
            className="!bg-[var(--new-primary)] hover:!bg-[var(--new-primary)] text-white rounded-lg"
            icon={<EyeOutlined /> }
          >
            Preview Flow
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-6">
          <Card
            className="shadow-md hover:shadow-xl transition-all duration-300 border-0 rounded-xl overflow-hidden"
            bodyStyle={{ padding: 0 }}
          >
            <div className="p-6 bg-gradient-to-br from-blue-500 to-blue-600">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-blue-100 text-sm font-medium">
                      Total Delivered
                    </p>
                    <Tooltip
                      title="Total number of flows delivered to users successfully"
                      placement="top"
                    >
                      <InfoCircleOutlined className="text-blue-200 text-xs cursor-help hover:text-white transition-colors" />
                    </Tooltip>
                  </div>
                  <p className="text-4xl font-bold text-white mb-1">
                    {statsData.delivered.toLocaleString()}
                  </p>
                  <p className="text-blue-100 text-xs">Messages sent successfully</p>
                </div>
                <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                  <SendOutlined className="text-2xl text-white" />
                </div>
              </div>
            </div>
          </Card>

          <Card
            className="shadow-md hover:shadow-xl transition-all duration-300 border-0 rounded-xl overflow-hidden"
            bodyStyle={{ padding: 0 }}
          >
            <div className="p-6 bg-gradient-to-br from-green-500 to-green-600">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-green-100 text-sm font-medium">Completed</p>
                    <Tooltip
                      title="Total number of flows completed by users"
                      placement="top"
                    >
                      <InfoCircleOutlined className="text-green-200 text-xs cursor-help hover:text-white transition-colors" />
                    </Tooltip>
                  </div>
                  <p className="text-4xl font-bold text-white mb-1">
                    {statsData.completed.toLocaleString()}
                  </p>
                  <p className="text-green-100 text-xs">Flows finished by users</p>
                </div>
                <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                  <CheckCircleOutlined className="text-2xl text-white" />
                </div>
              </div>
            </div>
          </Card>

          <Card
            className="shadow-md hover:shadow-xl transition-all duration-300 border-0 rounded-xl overflow-hidden"
            bodyStyle={{ padding: 0 }}
          >
            <div className="p-6 bg-gradient-to-br from-purple-500 to-purple-600">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-purple-100 text-sm font-medium">
                      Completion Ratio
                    </p>
                    <Tooltip
                      title={
                        <div>
                          <div className="font-medium mb-1">
                            Completion Ratio Formula:
                          </div>
                          <div>(Completed ÷ Delivered) × 100</div>
                          <div className="mt-2 text-xs opacity-90">
                            Example: ({statsData.completed} ÷{' '}
                            {statsData.delivered || 1}) × 100 ={' '}
                            {statsData.completionRatio}%
                          </div>
                        </div>
                      }
                      placement="top"
                    >
                      <InfoCircleOutlined className="text-purple-200 text-xs cursor-help hover:text-white transition-colors" />
                    </Tooltip>
                  </div>
                  <p className="text-4xl font-bold text-white mb-1">
                    {statsData.completionRatio.toLocaleString(undefined, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 1,
                    })}
                    %
                  </p>
                  <p className="text-purple-100 text-xs">Overall success rate</p>
                </div>
                <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                  <PercentageOutlined className="text-2xl text-white" />
                </div>
              </div>
            </div>
          </Card>
        </div>

        <Card className="shadow-lg border-0 rounded-xl" bodyStyle={{ padding: '24px' }}>
          <div className="flex flex-col gap-4 mb-6">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1">
                <Input
                  size="large"
                  placeholder="Search by contact name or phone..."
                  prefix={<SearchOutlined className="text-gray-400" />}
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                  className="rounded-lg"
                  allowClear
                />
              </div>

              <Space size="middle" className="flex-wrap">
                <Select
                  size="large"
                  value={statusFilter}
                  onChange={setStatusFilter}
                  className="w-full sm:w-44"
                  options={STATUS_OPTIONS}
                />

                {/* <Select
                  size="large"
                  allowClear
                  showSearch
                  placeholder="Filter by broadcast or sequence"
                  value={
                    selectedSource
                      ? `${selectedSource.source_type}:${selectedSource.source_id}`
                      : undefined
                  }
                  onChange={handleSourceFilterChange}
                  loading={isSourcesLoading}
                  options={sourceSelectOptions}
                  optionLabelProp="display"
                  filterOption={filterSourceOption}
                  className="w-full sm:w-64"
                  dropdownMatchSelectWidth={false}
                  notFoundContent={
                    isSourcesLoading ? 'Loading sources…' : 'No matching sources'
                  }
                /> */}

                <RangePicker
                  size="large"
                  value={dateRange}
                  onChange={handleDateChange}
                  className="w-full sm:w-auto rounded-lg"
                  format={DATE_FORMAT}
                  placeholder={['Start Date', 'End Date']}
                  allowClear
                />

                {/* Actions Dropdown replaces old download button */}
                <Dropdown menu={menu} placement="bottomRight" trigger={['click']}>
                  <Button
                    size="large"
                    loading={isDownloading}
                    className="!bg-[var(--new-primary)] hover:!bg-[var(--new-primary)] !text-white rounded-lg shadow-md hover:shadow-lg transition-all flex items-center"
                  >
                    <Options stroke="#ffffff" />
                    Actions
                    <Chevron color="#ffffff"/>
                  </Button>
                </Dropdown>
              </Space>
            </div>
          </div>

          <div className="mb-4">
            <p className="text-sm text-gray-600">
              Showing{' '}
              <span className="font-semibold text-gray-900">{tableData.length}</span>{' '}
              of{' '}
              <span className="font-semibold text-gray-900">
                {displayedTotal}
              </span>{' '}
              results {hideDuplicates ? '(duplicates hidden)' : ''}
            </p>
            {isFlowLoading ? (
              <p className="text-xs text-gray-500 mt-1">
                Loading flow definition to build question columns…
              </p>
            ) : null}
          </div>

          <div className="overflow-x-auto -mx-6 px-6">
            <Table
              columns={columns}
              dataSource={tableData}
              loading={isLoading}
              pagination={{
                current: pagination.current,
                pageSize: 10,
                total: displayedTotal,
                showSizeChanger: false,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
                className: 'mt-4',
              }}
              onChange={handleTableChange}
              className="flow-insights-table h-full"
              scroll={{ x: 'max-content', y: 500 }}
              locale={{
                emptyText: (
                  <div className="py-12">
                    <p className="text-gray-400 text-lg">
                      {isLoading ? 'Loading data…' : 'No data found'}
                    </p>
                    <p className="text-gray-400 text-sm mt-2">
                      {isLoading
                        ? 'Fetching flow insights, please wait.'
                        : 'Try adjusting your filters'}
                    </p>
                  </div>
                ),
              }}
            />
          </div>
        </Card>

        <Modal
          title="Flow Preview"
          width={600}
          open={isPreviewVisible}              // AntD v5: 'open' instead of 'visible'
          footer={null}
          onCancel={() => setIsPreviewVisible(false)}
          bodyStyle={{ padding: 0 }}
          destroyOnClose
        >
          <WhatsAppFlowPreview flow_json={parsedFlowJson} />
        </Modal>
      </div>

      {/* NOTE: If not using styled-jsx/Next.js, move these styles to a CSS/LESS file */}
      <style jsx global>{`
        .flow-insights-table .ant-table-thead > tr > th {
          background: #f8fafc;
          font-weight: 600;
          color: #1e293b;
          border-bottom: 2px solid #e2e8f0;
          padding: 16px;
        }
        .flow-insights-table .ant-table-tbody > tr:hover > td {
          background: #f8fafc;
        }
        .flow-insights-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f1f5f9;
          padding: 16px;
        }
        .flow-insights-table .ant-pagination-item-active {
          border-color: #3b82f6;
          background: #3b82f6;
        }
        .flow-insights-table .ant-pagination-item-active a {
          color: white;
        }
        .flow-insights-table .ant-table-column-sorters {
          display: inline-flex;
          align-items: center;
          cursor: default;
        }
        .flow-insights-table .ant-table-column-sorter {
          display: none;
        }
        .flow-insights-table .ant-table-body {
          overflow-y: auto !important;
          max-height: 500px;
        }
        .flow-insights-table .ant-spin-container {
          min-height: 500px;
        }
        .flow-insights-table .ant-table-placeholder {
          min-height: 500px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        @media (max-width: 768px) {
          .flow-insights-table .ant-table-thead > tr > th {
            padding: 12px 8px;
            font-size: 13px;
          }
          .flow-insights-table .ant-table-tbody > tr > td {
            padding: 12px 8px;
            font-size: 13px;
          }
        }
      `}</style>
    </div>
  );
};

export default FlowInsights;
